/**
 * Life4Earth V2 — Admin Console Shell
 *
 * Self-contained layout for system administration.
 * Renders OUTSIDE the main app shell (no LeftNav, no topbar).
 * Own header + sidebar nav + content area.
 *
 * English only (per CLAUDE.md: system admin UI stays in English).
 * Function over form — productivity, efficiency, clarity.
 */

import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import styles from './AdminShell.module.css';

// ── Sidebar navigation — grouped by functional area ──────────

interface NavItem { to: string; label: string; icon: string; exact?: boolean }
interface NavGroup { heading: string; items: NavItem[] }

const NAV_GROUPS: NavGroup[] = [
  {
    heading: 'Overview',
    items: [
      { to: '/admin',           label: 'Dashboard',        icon: '⊞', exact: true },
    ],
  },
  {
    heading: 'Identity & Access',
    items: [
      { to: '/admin/users',     label: 'Users',            icon: '⊕' },
      { to: '/admin/access',    label: 'Access Control',   icon: '⊗' },
      { to: '/admin/audit',     label: 'Audit Log',        icon: '⊜' },
      { to: '/admin/moderation', label: 'Moderation',      icon: '⚑' },
    ],
  },
  {
    heading: 'Content & Data',
    items: [
      { to: '/admin/landing',   label: 'Landing Page',     icon: '⊡' },
      { to: '/admin/geo',       label: 'Geographies',      icon: '◉' },
      { to: '/admin/rhythms',   label: 'Role Rhythms',     icon: '↻' },
      { to: '/admin/pillars',   label: 'Project Cleanup',   icon: '⊠' },
      { to: '/admin/content',   label: 'Content Cleanup',   icon: '⊟' },
      { to: '/admin/workspaces', label: 'Workspace Cleanup', icon: '⊡' },
      { to: '/admin/news',      label: 'News Settings',    icon: '⊞' },
      { to: '/admin/onboarding',label: 'Onboarding',       icon: '⊶' },
      { to: '/admin/activities',label: 'Impact Activities', icon: '◈' },
      { to: '/admin/embeds',    label: 'Embed Allowlist',  icon: '⊏' },
    ],
  },
  {
    heading: 'Platform Config',
    items: [
      { to: '/admin/platform',  label: 'Platform Settings',icon: '⊛' },
      { to: '/admin/features',  label: 'Feature Flags',    icon: '⊘' },
    ],
  },
  {
    heading: 'Appearance',
    items: [
      { to: '/admin/themes',    label: 'Themes',           icon: '⊚' },
      { to: '/admin/designer',  label: 'Theme Designer',   icon: '◎' },
      { to: '/admin/i18n',      label: 'Localization',     icon: '⊟' },
    ],
  },
  {
    heading: 'System',
    items: [
      { to: '/admin/api',       label: 'API Monitor',      icon: '⊙' },
    ],
  },
];

// ── L4E Wheel (compact) ─────────────────────────────────────

function L4EWheelMini() {
  return (
    <svg width="24" height="24" viewBox="-15 -15 158 143" fill="none">
      <path d="M107.574 43.0177C114.543 45.6668 120.513 41.244 121.466 35.3008C143.023 81.9018 107.62 125.992 68.0824 127.581C60.1377 125.508 59.8821 115.902 67.9663 112.171C96.0285 110.051 123.301 78.3774 107.574 43.0177Z" fill="#45BB47" />
      <path d="M10.0098 29.7194C17.397 28.0148 22.0431 30.756 24.0409 37.7128C42.416 10.9915 82.1631 7.09848 103.744 36.4919C111.364 43.2643 121.213 35.3171 116.242 26.863C89.3877 -10.6159 35.1914 -8.15106 10.0098 29.7194Z" fill="#106DD5" />
      <path d="M60.3411 112.197C55.9274 116.413 55.1608 123.185 60.3411 128C11.8595 122.586 -10.9062 77.4365 5.05298 37.7692C10.303 29.8679 20.8264 34.0835 19.6881 44.1039C8.35168 67.3929 19.8972 107.728 60.3411 112.197Z" fill="#D53541" />
      <path d="M64.1983 23.4648C41.7114 23.4648 23.4756 41.5478 23.4756 63.8463C23.4756 86.1447 41.7114 104.228 64.2216 104.228C86.7085 104.228 104.944 86.1447 104.944 63.8463C104.944 41.5478 86.7085 23.4648 64.1983 23.4648ZM63.9428 88.3561C50.4227 88.3561 39.4813 77.5064 39.4813 64.0997C39.4813 50.6929 50.4227 39.8432 63.9428 39.8432C77.4628 39.8432 88.4043 50.6929 88.4043 64.0997C88.4043 77.5064 77.4628 88.3561 63.9428 88.3561Z" fill="white" />
    </svg>
  );
}

// ── Main Shell ───────────────────────────────────────────────

export default function AdminShell({ degradedMode = false }: { degradedMode?: boolean }) {
  const navigate = useNavigate();

  return (
    <div className={styles.shell}>
      {/* ── Sidebar ── */}
      <aside className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <button
            className={styles.brandBtn}
            onClick={() => navigate('/')}
            title="Back to app"
          >
            <L4EWheelMini />
          </button>
          <div className={styles.sidebarTitle}>
            <span className={styles.titleMain}>Admin</span>
            <span className={styles.titleSub}>System Console</span>
          </div>
        </div>

        <nav className={styles.nav}>
          {NAV_GROUPS.map((group) => (
            <div key={group.heading} className={styles.navGroup}>
              <div className={styles.navGroupHeading}>{group.heading}</div>
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.exact}
                  className={({ isActive }) =>
                    `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
                  }
                >
                  <span className={styles.navIcon}>{item.icon}</span>
                  <span className={styles.navLabel}>{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className={styles.sidebarFooter}>
          <button
            className={styles.backBtn}
            onClick={() => navigate('/')}
          >
            &larr; Back to App
          </button>
        </div>
      </aside>

      {/* ── Content ── */}
      <main className={styles.content}>
        {degradedMode && (
          <div className={styles.degradedBanner}>
            <strong>Degraded Mode</strong> — Security API is unavailable.
            Admin access is unverified. Some features may not work.
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
}
